import React, { useEffect, useRef, useState } from "react";

/**
 * Listă derulantă cu bife, pentru filtre unde se pot alege mai multe valori.
 * `value` = lista valorilor alese (goală = „Toate”); `onChange(listaNouă)`.
 */
export default function MultiSelect({ options, value, onChange, allLabel = "Toate", testId, minWidth = 180, summaryOf }) {
  // opțiunile pot fi text simplu sau { value, label } (ex. structuri: id + denumire)
  const opts = options.map(o => (typeof o === "string" ? { value: o, label: o } : o));
  const labelOf = (v) => (opts.find(o => o.value === v)?.label ?? v);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);

  const norm = (x) => String(x ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const tokens = norm(q).split(/\s+/).filter(Boolean);
  const visible = tokens.length ? opts.filter(o => tokens.every(t => norm(o.label).includes(t))) : opts;
  const toggle = (opt) => onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  const summary = summaryOf ? summaryOf(value) : value.length === 0 ? allLabel : value.length === 1 ? labelOf(value[0]) : `${value.length} selectate`;

  return (
    <div ref={ref} style={{ position: "relative", minWidth }} data-testid={testId}>
      <button type="button" className="select" aria-haspopup="listbox" aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 8 }}
        data-testid={testId ? `${testId}-button` : undefined}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
        <span aria-hidden="true" style={{ color: "#64748B", fontSize: 10, alignSelf: "center" }}>▾</span>
      </button>
      {open && (
        <div role="listbox" aria-multiselectable="true"
          style={{ position: "absolute", zIndex: 30, top: "calc(100% + 4px)", left: 0, minWidth: "100%", maxHeight: 260,
                   overflow: "auto", background: "white", border: "1px solid #E2E8F0", borderRadius: 6,
                   boxShadow: "0 8px 24px rgba(15,23,42,.12)", padding: 6 }}>
          <button type="button" className="btn btn-ghost" style={{ width: "100%", justifyContent: "flex-start", fontSize: 12, padding: "4px 8px" }}
            onClick={() => onChange([])} disabled={value.length === 0} data-testid={testId ? `${testId}-clear` : undefined}>
            {allLabel} (șterge selecția)
          </button>
          {opts.length > 8 && (
            <input className="input" autoFocus placeholder="Scrie pentru a căuta…" value={q} onChange={e => setQ(e.target.value)}
              style={{ margin: "4px 0", padding: "6px 8px", fontSize: 13 }} data-testid={testId ? `${testId}-search` : undefined} />
          )}
          {opts.length > 0 && visible.length === 0 && <div style={{ padding: "6px 8px", fontSize: 12, color: "#64748B" }}>Niciun rezultat.</div>}
          {opts.length === 0 && <div style={{ padding: "6px 8px", fontSize: 12, color: "#64748B" }}>Nicio opțiune disponibilă.</div>}
          {visible.map(o => (
            <label key={o.value} style={{ display: "flex", gap: 8, alignItems: "center", padding: "5px 8px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)}
                data-testid={testId ? `${testId}-opt-${o.value}` : undefined} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
